import { DestroyRef, Injectable, NgZone, inject, isDevMode, signal } from '@angular/core';
import {
  UserAgent,
  Registerer,
  RegistererState,
  Inviter,
  Invitation,
  RequestPendingError,
  Session,
  SessionState,
  Web,
} from 'sip.js';
import type {
  SessionDescriptionHandlerModifier,
  UserAgentOptions,
} from 'sip.js';
import type {
  SessionDescriptionHandlerOptions as WebSDHOptions,
} from 'sip.js/lib/platform/web/session-description-handler/session-description-handler-options';
import { Observable, Subject } from 'rxjs';
import { CallStatus } from '../models/call-status.enum';
import { SipCredentialsDto } from '../models/sip-credentials.model';
import { CallDirection } from '../models/call-direction.type';
import { IncomingCallInfo } from '../models/incoming-call-info.model';
import { CallStateStore } from './call-state.store';
import { DeviceService } from './device.service';
import { preferOpusModifier } from '../utils/sdp-utils';
import { isOutboundGatewayDestination } from '../utils/build-outbound-dial-destination';

const DEFAULT_MAX_RECONNECT_ATTEMPTS = 5;
const DEFAULT_INITIAL_RECONNECT_DELAY_MS = 2000;
const DEFAULT_MAX_RECONNECT_DELAY_MS = 30000;
const DEFAULT_REGISTRATION_EXPIRY = 300;
const DEFAULT_CALL_SETUP_ICE_GATHERING_TIMEOUT_MS = 1200;

/**
 * How long an unanswered incoming INVITE is allowed to keep ringing before
 * sip.js rejects it with 480. Its own default is 60s, which silently capped
 * the queue "max response time" setting (mod_fifo member timeout, up to 120s):
 * the agent leg died at 60s no matter what the queue was configured with.
 * Kept above that maximum so FreeSwitch stays the single authority on ring time.
 */
const NO_ANSWER_TIMEOUT_SECONDS = 130;

/**
 * SIP 603 Decline - the response an agent's explicit "no" is sent as. mod_sofia maps it to
 * CALL_REJECTED, the one cause mod_callcenter treats as a rejection rather than an unanswered call.
 */
const SIP_DECLINE_STATUS_CODE = 603;

/**
 * Health check interval: runs every 60 seconds to verify
 * that the registerer is still in Registered state.
 * If not, triggers a re-register attempt.
 */
const HEALTH_CHECK_INTERVAL_MS = 60_000;

/** Max consecutive health check failures before giving up */
const MAX_HEALTH_CHECK_FAILURES = 3;

/**
 * Connection intent for the SIP service.
 * - 'maintain': actively keep connection alive (reconnect on drop)
 * - 'shuttingDown': gracefully tearing down
 * - 'idle': no active connection intent
 */
type ConnectionIntent = 'maintain' | 'shuttingDown' | 'idle';

/**
 * Core SIP service -- the ONLY file that imports from 'sip.js'.
 * Manages the UserAgent lifecycle, registration, call sessions,
 * auto-reconnect with exponential backoff, and remote audio.
 */
@Injectable({ providedIn: 'root' })
export class SipCoreService {
  private readonly store = inject(CallStateStore);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService = inject(DeviceService);

  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private currentSession: Session | null = null;
  private credentials: SipCredentialsDto | null = null;
  private connectionIntent: ConnectionIntent = 'idle';
  private agentAvailable = false;
  private isRegistering = false;

  private audioElement: HTMLAudioElement | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null;
  private consecutiveHealthCheckFailures = 0;

  /**
   * When true, the next session state transition to Establishing
   * will immediately cancel the outbound call.
   * This handles the race condition where hangup() is called before
   * the SIP INVITE has been sent (session still in Initial state).
   */
  private cancelPending = false;

  /** Stored listener references for proper cleanup */
  private sessionStateListener: ((state: SessionState) => void) | null = null;
  private registererStateListener:
    | ((state: RegistererState) => void)
    | null = null;

  /**
   * The agent's extension number, falling back to their canonical user name (carried on the
   * credentials as `displayName`) when they have none assigned. Doubles as the identity key
   * call-tab-sync matches browser tabs on, so it must never be empty for a registered agent — the
   * backend always populates `displayName`, so the fallback guarantees that.
   *
   * Deliberately NOT falling back to `username`: that is the per-session SIP auth name
   * (`__webrtc__<userName>`), which is opaque and must never be shown to the user.
   */
  readonly agentExtension = signal<string | null>(null);

  private readonly incomingCall$ = new Subject<IncomingCallInfo>();
  private readonly callEnded$ = new Subject<{
    sessionId: string;
    wasEstablished: boolean;
    source: 'local' | 'remote' | 'network';
  }>();
  private lastEndedSessionId: string | null = null;

  private readonly credentialsRejected$ = new Subject<number | null>();

  readonly onIncomingCall$: Observable<IncomingCallInfo> =
    this.incomingCall$.asObservable();
  readonly onCallEnded$: Observable<{
    sessionId: string;
    wasEstablished: boolean;
    source: 'local' | 'remote' | 'network';
  }> = this.callEnded$.asObservable();

  /**
   * Emits the rejection status code when a REGISTER is refused for a reason retrying cannot fix,
   * meaning the credentials this service holds are no longer usable.
   *
   * Reported rather than handled here: fetching credentials belongs to the facade, and so does the
   * decision of whether to retry. Emitting deliberately does not transition the store, so a
   * successful re-provision never flashes an error state at the agent.
   */
  readonly onCredentialsRejected$: Observable<number | null> =
    this.credentialsRejected$.asObservable();

  constructor() {
    this.destroyRef.onDestroy(() => {
      void this.destroy();
      this.incomingCall$.complete();
      this.callEnded$.complete();
      this.credentialsRejected$.complete();
    });
  }

  // ─── Public API ──────────────────────────────────────────────────

  async initialize(credentials: SipCredentialsDto): Promise<void> {
    if (this.userAgent || this.registerer || this.currentSession) {
      await this.destroy();
    }

    this.credentials = credentials;
    // Falls back to displayName, not username: username is now the opaque per-session SIP auth name
    // (`__webrtc__<userName>`), which must never surface in the UI. displayName carries the agent's
    // canonical user name for exactly this case -- an agent with no extension number assigned.
    this.agentExtension.set(
      credentials.extension ?? credentials.displayName ?? null
    );
    this.connectionIntent = 'maintain';
    this.ensureAudioElement();

    this.store.transition(CallStatus.Registering);

    try {
      const aor =
        credentials.registeredAor ??
        `sip:${credentials.username}@${credentials.domain}`;
      const uri = UserAgent.makeURI(aor);
      if (!uri) {
        throw new Error(`Invalid SIP URI: ${aor}`);
      }

      const options: UserAgentOptions = {
        uri,
        displayName: credentials.displayName ?? credentials.username,
        authorizationUsername:
          credentials.authUsername ?? credentials.username,
        authorizationPassword: credentials.password,
        transportOptions: {
          server: credentials.serverUri,
        } as Record<string, unknown>,
        sessionDescriptionHandlerFactory:
          Web.defaultSessionDescriptionHandlerFactory(
            constraints => this.deviceService.getCallMediaStream(constraints)
          ),
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: this.buildRtcConfig(credentials),
          iceGatheringTimeout: this.getIceGatheringTimeoutMs(),
        },
        delegate: {
          onConnect: () => this.onTransportConnect(),
          onDisconnect: (error?: Error) => this.onTransportDisconnect(error),
          onInvite: (invitation: Invitation) =>
            this.onIncomingInvite(invitation),
        },
        noAnswerTimeout: NO_ANSWER_TIMEOUT_SECONDS,
        logBuiltinEnabled: isDevMode(),
        logLevel: isDevMode() ? 'warn' : 'error',
      };

      this.userAgent = new UserAgent(options);
      await this.userAgent.start();
    } catch (err) {
      this.handleError('initialize', err);

      // Cleanup partially created resources on failure
      if (this.userAgent) {
        try {
          await this.userAgent.stop();
        } catch {
          // Best effort
        }
        this.userAgent = null;
      }
      this.removeAudioElement();
      this.credentials = null;
      this.connectionIntent = 'idle';

      this.store.transition(CallStatus.Failed);
    }
  }

  async makeCall(destination: string, extraHeaders?: string[]): Promise<void> {
    if (!this.userAgent || !this.credentials) {
      this.logWarn('Not initialized');
      return;
    }

    const targetUri = this.buildTargetUri(destination);
    if (!targetUri) {
      this.store.setError('Invalid call destination');
      return;
    }

    const target = UserAgent.makeURI(targetUri);
    if (!target) {
      this.store.setError('Invalid call destination');
      return;
    }

    this.store.transition(CallStatus.Trying);

    try {
      const constraintOptions = this.buildMediaConstraints();
      const modifiers = this.buildModifiers();
      const inviter = new Inviter(this.userAgent, target, {
        sessionDescriptionHandlerOptions: constraintOptions,
        sessionDescriptionHandlerModifiers: modifiers,
        sessionDescriptionHandlerModifiersReInvite: modifiers,
        ...(extraHeaders ? { extraHeaders } : {}),
      });

      // `destination` is the value CallFacadeService already ran through buildOutboundDialDestination(),
      // so classifying it here reads back the dial decision that was made instead of repeating it. Only a
      // gateway-routed destination reaches the backend handler that stamps this A-leg's SIP Call-ID.
      this.setupSession(
        inviter,
        'outbound',
        this.extractSipCallId(inviter),
        null,
        isOutboundGatewayDestination(destination),
      );
      await inviter.invite({
        requestDelegate: {
          onProgress: () => {
            this.zone.run(() => {
              if (this.store.callStatus() === CallStatus.Trying) {
                this.store.transition(CallStatus.Ringing);
              }
            });
          },
        },
      });
    } catch (err) {
      // If cancel was pending and invite() rejected as a result,
      // the cancel flow (onSessionStateChange) may have already
      // cleaned up.  Reset the flag and ensure a clean state.
      if (this.cancelPending) {
        this.cancelPending = false;
        this.cleanupSession();
        // HangingUp → Terminated → Registered/Idle
        this.store.transition(CallStatus.Terminated);
        this.transitionToAvailableState();
        return;
      }

      this.handleError('makeCall', err);
      this.cleanupSession();
      this.store.transition(CallStatus.Failed);
    }
  }

  async answerCall(): Promise<void> {
    if (
      !this.currentSession ||
      !(this.currentSession instanceof Invitation)
    ) {
      this.logWarn('No pending invitation to answer');
      return;
    }

    try {
      const constraintOptions = this.buildMediaConstraints();
      await (this.currentSession as Invitation).accept({
        sessionDescriptionHandlerOptions: constraintOptions,
        sessionDescriptionHandlerModifiers: this.buildModifiers(),
      });
    } catch (err) {
      this.handleError('answerCall', err);
      this.store.transition(CallStatus.Failed);
    }
  }

  /**
   * Releases a still-ringing incoming call as a deliberate decline rather than a plain release.
   *
   * <p>The distinction is not cosmetic: it is the only thing that tells FreeSWITCH the person said no.
   * A bare {@link Invitation.reject} sends SIP 480, which mod_sofia maps to NO_USER_RESPONSE and
   * mod_callcenter then files under "no answer" - indistinguishable from a phone that simply rang out,
   * so it consumes the agent's no-answer budget and picks up the no-answer retry delay instead of the
   * reject one. 603 Decline maps to CALL_REJECTED, which is what a decline actually is.</p>
   *
   * <p>Anything that is not a ringing invitation falls through to {@link hangup}, whose per-state
   * handling (bye / cancel / pending-cancel) is what those cases need.</p>
   */
  async declineIncoming(): Promise<void> {
    return this.hangup(SIP_DECLINE_STATUS_CODE);
  }

  async hangup(declineStatusCode?: number): Promise<void> {
    if (!this.currentSession) {
      return;
    }

    const session = this.currentSession;
    const state = session.state;

    try {
      this.store.transition(CallStatus.HangingUp);

      if (state === SessionState.Established) {
        await session.bye();
      } else if (
        session instanceof Invitation &&
        state === SessionState.Initial
      ) {
        // No status code means a release with no verdict attached, and sip.js defaults that to 480.
        await session.reject(
          declineStatusCode ? { statusCode: declineStatusCode } : undefined,
        );
      } else if (
        session instanceof Inviter &&
        state === SessionState.Establishing
      ) {
        await session.cancel();
      } else if (
        session instanceof Inviter &&
        state === SessionState.Initial
      ) {
        // INVITE not yet sent -- mark for cancellation so that
        // onSessionStateChange(Establishing) will cancel immediately.
        this.cancelPending = true;
        return;
      }
    } catch (err) {
      this.handleError('hangup', err);
    } finally {
      if (!this.cancelPending) {
        this.emitCallEnded(session, 'local');
        // Guard against double-transition when session already terminated
        const currentStatus = this.store.callStatus();
        if (currentStatus !== CallStatus.Terminated
          && currentStatus !== CallStatus.Registered
          && currentStatus !== CallStatus.Idle) {
          this.store.transition(CallStatus.Terminated);
        }
        this.cleanupSession();
        this.transitionToAvailableState();
      }
    }
  }

  async toggleHold(): Promise<void> {
    if (!this.currentSession) {
      return;
    }

    const session = this.currentSession;
    if (session.state !== SessionState.Established) {
      return;
    }

    const isCurrentlyHeld = this.store.currentSession()?.isHeld ?? false;

    try {
      const sdhOptions: WebSDHOptions = { hold: !isCurrentlyHeld };
      await session.invite({
        sessionDescriptionHandlerOptions: sdhOptions,
      });

      this.store.setHeld(!isCurrentlyHeld);
      this.store.transition(
        isCurrentlyHeld ? CallStatus.Active : CallStatus.Held
      );
    } catch (err) {
      this.handleError('toggleHold', err);
    }
  }

  toggleMute(): void {
    if (!this.currentSession) {
      return;
    }

    const sdh = this.currentSession.sessionDescriptionHandler;
    if (!(sdh instanceof Web.SessionDescriptionHandler)) {
      return;
    }

    const isCurrentlyMuted =
      this.store.currentSession()?.isMuted ?? false;
    sdh.enableSenderTracks(isCurrentlyMuted);
    this.store.setMuted(!isCurrentlyMuted);
  }

  sendDtmf(tones: string): boolean {
    if (!this.currentSession) {
      return false;
    }

    const sdh = this.currentSession.sessionDescriptionHandler;
    if (!(sdh instanceof Web.SessionDescriptionHandler)) {
      return false;
    }

    return sdh.sendDtmf(tones, { duration: 100, interToneGap: 70 });
  }

  async destroy(): Promise<void> {
    this.connectionIntent = 'shuttingDown';
    this.isRegistering = false;
    this.clearReconnectTimer();
    this.stopHealthCheck();

    if (this.currentSession) {
      try {
        await this.hangup();
      } catch {
        // Best effort cleanup
      }
    }

    if (this.registerer) {
      if (this.registererStateListener) {
        this.registerer.stateChange.removeListener(
          this.registererStateListener
        );
        this.registererStateListener = null;
      }
      try {
        await this.registerer.unregister();
        await this.registerer.dispose();
      } catch {
        // Best effort cleanup
      }
      this.registerer = null;
    }

    if (this.userAgent) {
      try {
        await this.userAgent.stop();
      } catch {
        // Best effort cleanup
      }
      this.userAgent = null;
    }

    this.credentials = null;
    this.agentExtension.set(null);
    this.agentAvailable = false;
    this.removeAudioElement();
    this.store.reset();
    this.connectionIntent = 'idle';
  }

  async setAgentAvailability(
    available: boolean,
    options: { keepRegistration?: boolean } = {}
  ): Promise<void> {
    const wasAvailable = this.agentAvailable;
    this.agentAvailable = available;

    if (!this.userAgent || this.connectionIntent !== 'maintain') {
      return;
    }

    if (!available) {
      this.stopHealthCheck();

      if (!options.keepRegistration) {
        if (this.registerer?.state === RegistererState.Registered) {
          await this.registerer.unregister();
        }
        this.transitionToIdleWhenPossible();
      }
      return;
    }

    if (this.registerer?.state === RegistererState.Registered) {
      if (!wasAvailable) {
        this.store.resetReconnectAttempts();
        this.startHealthCheck();
      }
      return;
    }

    if (this.userAgent.transport.isConnected()) {
      await this.registerWithServer();
    }
  }

  isTransportConnected(): boolean {
    return !!this.userAgent && this.userAgent.transport.isConnected();
  }

  // ─── Transport Events ──────────────────────────────────────────

  private onTransportConnect(): void {
    this.zone.run(() => {
      if (this.connectionIntent !== 'maintain') {
        return;
      }

      this.store.setError(null);
      this.clearReconnectTimer();
      this.store.resetReconnectAttempts();
      if (this.agentAvailable) {
        void this.registerWithServer();
      } else {
        this.transitionToIdleWhenPossible();
      }
    });
  }

  private onTransportDisconnect(error?: Error): void {
    this.zone.run(() => {
      if (this.connectionIntent !== 'maintain') {
        return;
      }

      if (error) {
        this.store.setError(error.message);
      }

      if (this.currentSession) {
        this.emitCallEnded(this.currentSession, 'network');
        this.store.transition(CallStatus.Terminated);
        this.cleanupSession();
      }

      this.attemptReconnect();
    });
  }

  // ─── Registration ──────────────────────────────────────────────

  private async registerWithServer(): Promise<void> {
    if (
      !this.userAgent ||
      this.connectionIntent !== 'maintain' ||
      !this.agentAvailable ||
      this.isRegistering
    ) {
      return;
    }

    this.isRegistering = true;
    this.store.transition(CallStatus.Registering);

    try {
      if (!this.registerer) {
        this.registerer = new Registerer(this.userAgent, {
          expires:
            this.credentials?.registrationExpiry ??
            DEFAULT_REGISTRATION_EXPIRY,
        });

        this.registererStateListener = (state: RegistererState) => {
          this.zone.run(() => this.onRegistererStateChange(state));
        };
        this.registerer.stateChange.addListener(
          this.registererStateListener
        );
      }

      await this.registerer.register({
        requestDelegate: {
          onAccept: () => {
            this.zone.run(() => {
              this.store.setError(null);
              this.clearReconnectTimer();
              this.store.resetReconnectAttempts();
              this.consecutiveHealthCheckFailures = 0;
              this.store.transition(CallStatus.Registered);
              this.startHealthCheck();
            });
          },
          onReject: response => {
            this.zone.run(() => {
              const statusCode = response.message.statusCode;
              // 401/407 are auth challenges handled by sip.js internally
              if (statusCode === 401 || statusCode === 407) {
                return;
              }

              // The browser authenticates with a per-session secret that is revoked when the
              // agent's active session row goes away (logout, idle timeout), and FreeSWITCH answers
              // a REGISTER for an auth name it can no longer resolve with 403. Re-registering with
              // the same credentials can never recover from that, so the agent would stay silently
              // unreachable until a page reload. Hand it to the facade, which re-fetches
              // credentials for the current session.
              //
              // Any non-challenge rejection is treated this way rather than matching on 403
              // specifically: the recovery is harmless when the credentials were in fact fine, and
              // depending on the exact code FreeSWITCH picks for an unknown user would make this
              // fail silently again if that ever changed.
              this.logWarn(
                `SIP registration rejected (${statusCode ?? 'no status code'}); requesting fresh credentials`
              );
              this.credentialsRejected$.next(statusCode ?? null);
            });
          },
        },
      });
    } catch (err) {
      if (err instanceof RequestPendingError) {
        this.logWarn('register skipped: a REGISTER is already in progress');
      } else {
        this.handleError('register', err);
        this.store.transition(CallStatus.Failed);
      }
    } finally {
      this.isRegistering = false;
    }
  }

  private onRegistererStateChange(state: RegistererState): void {
    if (this.connectionIntent === 'shuttingDown') {
      return;
    }

    switch (state) {
      case RegistererState.Registered:
        this.clearReconnectTimer();
        this.store.resetReconnectAttempts();
        this.store.transition(CallStatus.Registered);
        break;
      case RegistererState.Unregistered:
      case RegistererState.Terminated:
        if (this.connectionIntent === 'maintain' && this.agentAvailable) {
          this.attemptReconnect();
        } else {
          this.transitionToIdleWhenPossible();
        }
        break;
    }
  }

  // ─── Incoming Calls ────────────────────────────────────────────

  private onIncomingInvite(invitation: Invitation): void {
    this.zone.run(() => {
      if (!this.agentAvailable) {
        invitation.reject({ statusCode: 486 }).catch(err => {
          this.logWarn(`Failed to reject invite while unavailable: ${err}`);
        });
        return;
      }

      // Reject new invite if a call is already in progress
      if (this.currentSession) {
        invitation.reject({ statusCode: 486 }).catch(err => {
          this.logWarn(`Failed to reject concurrent invite: ${err}`);
        });
        return;
      }

      this.store.transition(CallStatus.Ringing);
      const sipCallId = this.extractSipCallId(invitation);
      const queueName = this.extractQueueName(invitation);
      // Inbound is always stamped: the backend records this agent's B-leg Call-ID for queue, direct
      // and internal calls alike (an internal call stamps the *called* segment, which is this one).
      this.setupSession(invitation, 'inbound', sipCallId, queueName, true);

      const remoteUri =
        invitation.remoteIdentity?.uri?.toString() ?? 'unknown';
      const remoteDisplayName =
        invitation.remoteIdentity?.displayName ?? '';
      const isInternal = this.extractIsInternalCall(invitation);
      const transferFromAgentName = this.extractTransferFromAgentName(invitation);
      const transferFromQueueName = this.extractTransferFromQueueName(invitation);

      this.incomingCall$.next({
        remoteUri,
        remoteDisplayName,
        isInternal,
        queueName,
        transferFromAgentName,
        transferFromQueueName,
        sipCallId,
      });
    });
  }

  // ─── Session Management ────────────────────────────────────────

  private setupSession(
    session: Session,
    direction: CallDirection,
    sipCallId: string | null = null,
    queueName: string | null = null,
    backendStampsAgentLegCallId = false,
  ): void {
    const previousSession = this.currentSession;
    if (this.sessionStateListener && previousSession) {
      previousSession.stateChange.removeListener(
        this.sessionStateListener
      );
      this.sessionStateListener = null;
    }

    this.currentSession = session;
    this.lastEndedSessionId = null;

    const remoteUri =
      session.remoteIdentity?.uri?.toString() ?? 'unknown';
    const remoteDisplayName =
      session.remoteIdentity?.displayName ?? '';

    this.store.setSession({
      id: session.id,
      remoteUri,
      remoteDisplayName,
      direction,
      startedAt: new Date(),
      isMuted: false,
      isHeld: false,
      sipCallId,
      backendStampsAgentLegCallId,
      queueName,
    });

    this.sessionStateListener = (state: SessionState) => {
      this.zone.run(() => this.onSessionStateChange(state));
    };
    session.stateChange.addListener(this.sessionStateListener);

    session.delegate = {
      onBye: () => {
        this.zone.run(() => {
          this.emitCallEnded(session, 'remote');
          this.store.transition(CallStatus.Terminated);
          this.cleanupSession();
          this.transitionToAvailableState();
        });
      },
    };
  }

  private onSessionStateChange(state: SessionState): void {
    switch (state) {
      case SessionState.Establishing:
        if (this.cancelPending) {
          this.cancelPending = false;
          if (this.currentSession instanceof Inviter) {
            this.currentSession.cancel().catch(err => {
              this.handleError('cancel-pending', err);
              // Ensure cleanup happens even if cancel() fails
              this.store.transition(CallStatus.Terminated);
              this.cleanupSession();
              this.transitionToAvailableState();
            });
          }
          return;
        }
        if (this.store.callStatus() === CallStatus.Trying) {
          this.store.transition(CallStatus.Ringing);
        }
        break;
      case SessionState.Established:
        this.store.transition(CallStatus.Active);
        this.store.setSession({
          ...this.store.currentSession()!,
          answeredAt: new Date(),
        });
        this.attachRemoteAudio();
        this.enableLocalSenderAudioTracks();
        break;
      case SessionState.Terminated:
        this.emitCallEnded(this.currentSession, 'remote');
        this.store.transition(CallStatus.Terminated);
        this.cleanupSession();
        this.transitionToAvailableState();
        break;
    }
  }

  private cleanupSession(): void {
    this.cancelPending = false;
    if (this.currentSession && this.sessionStateListener) {
      this.currentSession.stateChange.removeListener(
        this.sessionStateListener
      );
      this.sessionStateListener = null;
    }
    this.currentSession = null;
    this.store.setSession(null);
    this.detachRemoteAudio();
  }

  // ─── Audio Element ─────────────────────────────────────────────

  private ensureAudioElement(): void {
    if (this.audioElement) {
      return;
    }

    this.audioElement = document.createElement('audio');
    this.audioElement.id = 'telephony-remote-audio';
    this.audioElement.autoplay = true;
    this.audioElement.style.display = 'none';
    document.body.appendChild(this.audioElement);
  }

  private removeAudioElement(): void {
    if (this.audioElement) {
      this.audioElement.srcObject = null;
      this.audioElement.remove();
      this.deviceService.setActiveAudioElement(null);
      this.audioElement = null;
    }
  }

  private attachRemoteAudio(): void {
    if (!this.currentSession || !this.audioElement) {
      return;
    }

    const sdh = this.currentSession.sessionDescriptionHandler;
    if (!(sdh instanceof Web.SessionDescriptionHandler)) {
      return;
    }

    this.audioElement.srcObject = sdh.remoteMediaStream;
    this.audioElement.play().catch(err => {
      this.logWarn(`Audio play failed: ${err}`);
    });

    // Register audio element with DeviceService for live output switching
    this.deviceService.setActiveAudioElement(this.audioElement);

    const outputId = this.deviceService.getSelectedAudioOutputId();
    if (outputId && this.deviceService.sinkIdSupported()) {
      void this.deviceService.selectAudioOutput(outputId);
    }
  }

  private detachRemoteAudio(): void {
    if (this.audioElement) {
      this.audioElement.srcObject = null;
    }
    this.deviceService.setActiveAudioElement(null);
  }

  // ─── Reconnection ─────────────────────────────────────────────

  private attemptReconnect(): void {
    if (
      !this.userAgent ||
      !this.credentials ||
      this.connectionIntent !== 'maintain' ||
      this.reconnectTimer !== null
    ) {
      return;
    }

    const reconnectConfig = this.credentials.reconnect;
    const maxAttempts =
      reconnectConfig?.maxAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS;
    const initialDelayMs =
      reconnectConfig?.initialDelayMs ?? DEFAULT_INITIAL_RECONNECT_DELAY_MS;
    const maxDelayMs =
      reconnectConfig?.maxDelayMs ?? DEFAULT_MAX_RECONNECT_DELAY_MS;

    const attempts = this.store.reconnectAttempts();

    if (attempts >= maxAttempts) {
      this.store.transition(CallStatus.Failed);
      this.store.setError('Max reconnection attempts reached');
      return;
    }

    this.store.transition(CallStatus.Reconnecting);
    this.store.incrementReconnectAttempts();

    const delay = Math.min(
      initialDelayMs * Math.pow(2, attempts),
      maxDelayMs
    );

    // Run reconnect timer outside Angular zone to avoid CD triggers
    this.zone.runOutsideAngular(() => {
      this.reconnectTimer = setTimeout(async () => {
        this.reconnectTimer = null;

        if (
          !this.userAgent ||
          !this.credentials ||
          this.connectionIntent !== 'maintain'
        ) {
          return;
        }

        try {
          if (this.userAgent.transport.isConnected()) {
            await this.registerWithServer();

            // Re-check after async operation
            if (this.connectionIntent !== 'maintain') {
              return;
            }
            return;
          }

          await this.userAgent.reconnect();

          // Re-check after async operation
          if (this.connectionIntent !== 'maintain') {
            return;
          }
        } catch (err) {
          if (this.connectionIntent !== 'maintain') {
            return;
          }
          this.zone.run(() => {
            this.handleError('reconnect', err);
            this.attemptReconnect();
          });
        }
      }, delay);
    });
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ─── Registration Health Check ─────────────────────────────────

  private startHealthCheck(): void {
    this.stopHealthCheck();
    this.consecutiveHealthCheckFailures = 0;

    this.zone.runOutsideAngular(() => {
      this.healthCheckTimer = setInterval(() => {
        if (
          this.connectionIntent !== 'maintain' ||
          !this.agentAvailable ||
          !this.registerer ||
          !this.userAgent
        ) {
          return;
        }

        // If we think we're registered but registerer disagrees, re-register
        const storeStatus = this.store.callStatus();
        const isIdle =
          storeStatus === CallStatus.Registered ||
          storeStatus === CallStatus.Idle;

        if (
          isIdle &&
          this.registerer.state !== RegistererState.Registered &&
          this.userAgent.transport.isConnected()
        ) {
          if (
            this.consecutiveHealthCheckFailures >= MAX_HEALTH_CHECK_FAILURES
          ) {
            this.logWarn(
              `Health check: ${MAX_HEALTH_CHECK_FAILURES} consecutive`
              + ' re-registration failures, stopping health check'
            );
            this.stopHealthCheck();
            this.zone.run(() => {
              this.store.setError(
                'Registration health check failed repeatedly'
              );
              this.store.transition(CallStatus.Failed);
            });
            return;
          }

          this.consecutiveHealthCheckFailures++;
          this.zone.run(() => {
            void this.registerWithServer().catch(err => {
              this.logWarn(
                `Health check re-registration failed: ${String(err)}`
              );
            });
          });
        } else if (
          isIdle &&
          this.registerer.state === RegistererState.Registered
        ) {
          // Registration is healthy, reset failure counter
          this.consecutiveHealthCheckFailures = 0;
        }
      }, HEALTH_CHECK_INTERVAL_MS);
    });
  }

  private stopHealthCheck(): void {
    if (this.healthCheckTimer !== null) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────

  private extractSipCallId(session: Invitation | Inviter): string | null {
    try {
      // SIP Call-ID is the standard dialog identifier shared between FreeSwitch
      // (variable_sip_call_id in channel events) and SIP.js.
      //
      // Inbound: Invitation.request is the received INVITE.
      // Outbound: Inviter.request is the initial *outgoing* INVITE message. SIP.js builds it - and
      // stamps the Call-ID onto it - inside the Inviter constructor, before invite() puts it on the
      // wire, so reading it straight after "new Inviter(...)" yields the exact Call-ID FreeSwitch
      // sees on the agent A-leg. Falls back to null when absent, preserving the previous behaviour.
      return session.request.callId?.trim() || null;
    } catch {
      return null;
    }
  }

  private extractQueueName(invitation: Invitation): string | null {
    return this.extractEncodedHeader(invitation, 'X-CX-Queue-Name');
  }

  private extractIsInternalCall(invitation: Invitation): boolean {
    try {
      return (
        invitation.request.getHeader('X-CX-Internal-Call')?.trim().toLowerCase() ===
        'true'
      );
    } catch {
      return false;
    }
  }

  private extractTransferFromAgentName(invitation: Invitation): string | null {
    return this.extractEncodedHeader(invitation, 'X-CX-Transfer-From');
  }

  private extractTransferFromQueueName(invitation: Invitation): string | null {
    return this.extractEncodedHeader(invitation, 'X-CX-Transfer-From-Queue');
  }

  /**
   * Reads a percent-encoded custom header. The backend encodes these because FreeSwitch originate
   * variable values cannot carry spaces, which agent and queue names routinely have; a value that
   * is not valid encoding is passed through as-is rather than dropped.
   */
  private extractEncodedHeader(invitation: Invitation, header: string): string | null {
    try {
      const raw = invitation.request.getHeader(header)?.trim();
      if (!raw) {
        return null;
      }

      try {
        return decodeURIComponent(raw) || null;
      } catch {
        return raw;
      }
    } catch {
      return null;
    }
  }

  private buildTargetUri(destination: string): string | null {
    if (!this.credentials) {
      return null;
    }

    const normalized = destination.trim().replace(/^sip:/i, '');
    const userPart = normalized.split('@')[0]?.trim();
    if (!userPart) {
      return null;
    }

    const uriPattern = this.credentials.outgoingUriPattern;
    if (uriPattern && uriPattern.includes('{destination}')) {
      return uriPattern.replace('{destination}', userPart);
    }

    return `sip:${userPart}@${this.credentials.domain}`;
  }

  private buildModifiers(): SessionDescriptionHandlerModifier[] {
    if (this.credentials?.media?.preferredAudioCodec !== 'opus') {
      return [];
    }
    return [preferOpusModifier];
  }

  private buildRtcConfig(
    credentials: SipCredentialsDto
  ): RTCConfiguration {
    return {
      iceServers:
        credentials.iceServers?.map(s => ({
          urls: s.urls,
          username: s.username,
          credential: s.credential,
        })) ?? [],
    };
  }

  private buildMediaConstraints(): Record<string, unknown> {
    const selectedInputId = this.deviceService.getSelectedAudioInputId();
    const mediaPolicy = this.credentials?.media;

    const audioConstraint: boolean | MediaTrackConstraints =
      selectedInputId
        ? { deviceId: { ideal: selectedInputId } }
        : true;

    const enableVideo =
      mediaPolicy?.audioOnly === true
        ? false
        : (mediaPolicy?.video ?? false);

    return {
      constraints: {
        audio: audioConstraint,
        video: enableVideo,
      },
      iceGatheringTimeout: this.getIceGatheringTimeoutMs(),
      offerOptions: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      },
    };
  }

  private getIceGatheringTimeoutMs(): number {
    const configured =
      this.credentials?.media?.iceGatheringTimeoutMs ??
      DEFAULT_CALL_SETUP_ICE_GATHERING_TIMEOUT_MS;

    if (!Number.isFinite(configured)) {
      return DEFAULT_CALL_SETUP_ICE_GATHERING_TIMEOUT_MS;
    }

    return Math.min(
      5000,
      Math.max(250, Math.floor(configured))
    );
  }

  private transitionToAvailableState(): void {
    const isStillRegistered =
      this.connectionIntent === 'maintain' &&
      this.registerer?.state === RegistererState.Registered;

    this.store.transition(
      isStillRegistered ? CallStatus.Registered : CallStatus.Idle
    );
  }

  private emitCallEnded(
    session: Session | null,
    source: 'local' | 'remote' | 'network'
  ): void {
    if (!session) {
      return;
    }

    if (this.lastEndedSessionId === session.id) {
      return;
    }

    const activeStatus = this.store.callStatus();
    const sessionSnapshot = this.store.currentSession();
    const wasEstablished =
      activeStatus === CallStatus.Active ||
      activeStatus === CallStatus.Held ||
      (sessionSnapshot?.id === session.id && !!sessionSnapshot?.answeredAt);

    this.lastEndedSessionId = session.id;
    this.callEnded$.next({
      sessionId: session.id,
      wasEstablished,
      source,
    });
  }

  private transitionToIdleWhenPossible(): void {
    const status = this.store.callStatus();
    const canMoveToIdle =
      status === CallStatus.Registering ||
      status === CallStatus.Registered ||
      status === CallStatus.Terminated ||
      status === CallStatus.Failed ||
      status === CallStatus.Reconnecting;

    if (canMoveToIdle) {
      this.store.transition(CallStatus.Idle);
    }
  }

  private enableLocalSenderAudioTracks(): void {
    const sdh = this.currentSession?.sessionDescriptionHandler;
    if (!(sdh instanceof Web.SessionDescriptionHandler)) {
      return;
    }

    sdh.enableSenderTracks(true);
    this.store.setMuted(false);
  }

  private handleError(context: string, err: unknown): void {
    const message = err instanceof Error ? err.message : String(err);
    this.store.setError(message);
    this.logWarn(`${context}: ${message}`);
  }

  private logWarn(msg: string): void {
    if (isDevMode()) {
      console.warn(`[SipCoreService] ${msg}`);
    }
  }
}
